/**
 * ShareService — Unified share utility for the entire RGM app.
 * =============================================================
 *
 * ONE service, ONE contract. Every share surface — Track, Dashboard,
 * Feature, Recap, Winner, Hall of Champions, Magazine, Playlist, Beat,
 * Business Hub, Crew Post, Community Post, Live Room, Referral, etc. —
 * calls into this module. No screen should reimplement share logic.
 *
 * Public API
 * ----------
 *   ShareService.generate(node)            → Promise<Blob|null>
 *   ShareService.download(blob, filename)  → boolean
 *   ShareService.share(payload)            → Promise<"shared"|"downloaded"|"cancelled"|"failed">
 *   ShareService.copyLink(url)             → Promise<boolean>
 *   ShareService.whatsapp({text, url})     → boolean
 *   ShareService.facebook({url})           → boolean
 *   ShareService.x({text, url})            → boolean          // (Twitter)
 *   ShareService.threads({text, url})      → boolean
 *   ShareService.messenger({url, appId?})  → boolean
 *   ShareService.instagram({blob, name})   → Promise<boolean> // downloads → user uploads to Stories
 *   ShareService.sms({text, url})          → boolean
 *   ShareService.email({subject, body, url}) → boolean
 *
 * Availability probes
 * -------------------
 *   ShareService.canNativeShare()          → boolean
 *   ShareService.canNativeShareFiles()     → boolean
 *   ShareService.hasClipboard()            → boolean
 *
 * Payload shape (all fields optional except where noted)
 * ------------------------------------------------------
 *   {
 *     blob:     Blob (required for image-carrying shares)
 *     filename: string
 *     title:    string
 *     text:     string
 *     url:      string   // canonical https URL (OG-rich, deep-linkable)
 *   }
 *
 * URL contract
 * ------------
 * Callers MUST pass a canonical, OG-rich https URL for `url`. On the
 * web platform this yields the exact same preview as rgmstudio.org
 * because the backend already emits <meta property="og:*"> on every
 * share endpoint (`/api/share/track/:id`, `/api/share/feature/:id`,
 * `/api/share/winner/:entryId`, etc). When the native RN app is
 * eventually installed on-device, the same URLs are Universal Links —
 * OS will deep-link into the app; otherwise it falls back to the web.
 */

const isBrowser = typeof window !== "undefined";
const nav = isBrowser ? window.navigator : undefined;

// ---------------------------------------------------------------------------
// Availability probes
// ---------------------------------------------------------------------------

const canNativeShare = () => Boolean(nav && typeof nav.share === "function");

const canNativeShareFiles = () => {
  if (!nav || typeof nav.canShare !== "function") return false;
  try {
    // Cheapest probe — a 1-byte throwaway PNG file. Some browsers
    // support navigator.share(url) but NOT navigator.share({files}).
    const probe = new File([new Blob(["x"])], "probe.png", { type: "image/png" });
    return nav.canShare({ files: [probe] });
  } catch {
    return false;
  }
};

const hasClipboard = () => Boolean(nav && nav.clipboard && nav.clipboard.writeText);

// ---------------------------------------------------------------------------
// Local download helper (private)
// ---------------------------------------------------------------------------

const downloadBlob = (blob, filename) => {
  if (!isBrowser || !blob) return false;
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "rgm-share.png";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 2_000);
    return true;
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// Deep-link helpers (private) — wrap window.open with popup-blocker-safe args
// ---------------------------------------------------------------------------

const openIntent = (href) => {
  if (!isBrowser || !href) return false;
  const w = window.open(href, "_blank", "noopener,noreferrer");
  return !!w;
};

// ---------------------------------------------------------------------------
// Native share (image if supported, url fallback, download last resort)
// ---------------------------------------------------------------------------

const share = async ({ blob, filename, title, text, url }) => {
  // 1. Files-first if the browser supports it (mobile + Safari 16.4+).
  if (blob && canNativeShareFiles()) {
    try {
      const file = new File([blob], filename || "rgm-share.png", { type: blob.type || "image/png" });
      await nav.share({ files: [file], title, text, url });
      return "shared";
    } catch (e) {
      if (e && e.name === "AbortError") return "cancelled";
      // Fall through to url-only or download.
    }
  }
  // 2. URL-only share.
  if (url && canNativeShare()) {
    try {
      await nav.share({ title, text, url });
      return "shared";
    } catch (e) {
      if (e && e.name === "AbortError") return "cancelled";
    }
  }
  // 3. Download the image if we have one.
  if (blob) {
    return downloadBlob(blob, filename) ? "downloaded" : "failed";
  }
  return "failed";
};

// ---------------------------------------------------------------------------
// Copy link
// ---------------------------------------------------------------------------

const copyLink = async (url) => {
  if (!url) return false;
  if (hasClipboard()) {
    try {
      await nav.clipboard.writeText(url);
      return true;
    } catch { /* fall through */ }
  }
  // Fallback: transient textarea + document.execCommand("copy").
  if (!isBrowser) return false;
  try {
    const ta = document.createElement("textarea");
    ta.value = url;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand && document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch {
    return false;
  }
};

// ---------------------------------------------------------------------------
// Channel deep links
// ---------------------------------------------------------------------------

const composeMessage = ({ text, url }) =>
  [text, url].filter(Boolean).join(text && url ? " " : "");

const whatsapp = ({ text, url }) => openIntent(
  `https://wa.me/?text=${encodeURIComponent(composeMessage({ text, url }))}`
);

const facebook = ({ url }) => url
  ? openIntent(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)
  : false;

const x = ({ text, url }) => {
  const params = [];
  if (text) params.push(`text=${encodeURIComponent(text)}`);
  if (url) params.push(`url=${encodeURIComponent(url)}`);
  return openIntent(`https://twitter.com/intent/tweet?${params.join("&")}`);
};

const threads = ({ text, url }) => openIntent(
  `https://www.threads.net/intent/post?text=${encodeURIComponent(composeMessage({ text, url }))}`
);

const messenger = ({ url, appId }) => {
  if (!url) return false;
  // Facebook Messenger's share sheet on desktop needs an app_id; on
  // mobile the fb-messenger:// URI does the same job. We prefer the
  // web dialog when appId is present, else return false so the caller
  // can hide the button.
  if (appId) {
    const href = `https://www.facebook.com/dialog/send`
      + `?app_id=${encodeURIComponent(appId)}`
      + `&link=${encodeURIComponent(url)}`
      + `&redirect_uri=${encodeURIComponent(url)}`;
    return openIntent(href);
  }
  // Mobile app URI — will noop on desktop but tries anyway.
  return openIntent(`fb-messenger://share?link=${encodeURIComponent(url)}`);
};

const sms = ({ text, url }) => openIntent(
  `sms:?&body=${encodeURIComponent(composeMessage({ text, url }))}`
);

const email = ({ subject, body, url }) => {
  const s = encodeURIComponent(subject || "Check this out");
  const b = encodeURIComponent(composeMessage({ text: body, url }));
  return openIntent(`mailto:?subject=${s}&body=${b}`);
};

// ---------------------------------------------------------------------------
// iOS Instagram Stories handoff (best-effort, graceful fallback)
// ---------------------------------------------------------------------------

const isIOS = () => {
  if (!nav) return false;
  const ua = nav.userAgent || "";
  return /iPhone|iPad|iPod/.test(ua) || (/(Mac)/.test(ua) && nav.maxTouchPoints > 1);
};

// Convert a blob to a data URI so the ``instagram-stories://`` scheme
// can accept it as ``backgroundImage``. Returns null on failure.
const _blobToDataURI = (blob) => new Promise((resolve) => {
  try {
    const fr = new FileReader();
    fr.onload = () => resolve(typeof fr.result === "string" ? fr.result : null);
    fr.onerror = () => resolve(null);
    fr.readAsDataURL(blob);
  } catch {
    resolve(null);
  }
});

const instagram = async ({ blob, filename }) => {
  // Instagram has no URL scheme for pre-populated content on the web
  // in general. iOS Safari, however, honours the ``instagram-stories``
  // scheme when Instagram is installed. Try it first, fall back to the
  // pre-existing download → manual upload flow.
  if (!blob) return false;
  if (isIOS()) {
    try {
      const dataUri = await _blobToDataURI(blob);
      if (dataUri) {
        const href =
          "instagram-stories://share" +
          `?source_application=rgmstudio` +
          `&backgroundImage=${encodeURIComponent(dataUri)}`;
        // ``window.location`` beats ``window.open`` for custom schemes.
        try {
          window.location.href = href;
          // Optimistically report success — iOS will show its own
          // "Instagram is not installed" alert if the scheme fails.
          return true;
        } catch { /* fall through to download */ }
      }
    } catch { /* fall through to download */ }
  }
  return downloadBlob(blob, filename || "rgm-share.png");
};

// ---------------------------------------------------------------------------
// Image generation (thin re-export so callers depend on ShareService only)
// ---------------------------------------------------------------------------

let _exportNodeToBlob = null;
const _lazyExport = async () => {
  if (_exportNodeToBlob) return _exportNodeToBlob;
  const mod = await import("./shareExport.js");
  _exportNodeToBlob = mod.exportNodeToBlob;
  return _exportNodeToBlob;
};

const generate = async (node) => {
  if (!node) return null;
  const fn = await _lazyExport();
  return await fn(node);
};

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

const ShareService = {
  generate,
  download: downloadBlob,
  share,
  copyLink,
  whatsapp,
  facebook,
  x,
  threads,
  messenger,
  sms,
  email,
  instagram,
  canNativeShare,
  canNativeShareFiles,
  hasClipboard,
};

export default ShareService;
export {
  generate,
  downloadBlob as download,
  share,
  copyLink,
  whatsapp,
  facebook,
  x,
  threads,
  messenger,
  sms,
  email,
  instagram,
  canNativeShare,
  canNativeShareFiles,
  hasClipboard,
};
